import { useMediaQuery } from "@uidotdev/usehooks";
import { useState } from "react";
import {
  mergeEvents,
  onDay,
  startOfDay,
  startOfWeek,
} from "../../helpers/calendar";
import { colors } from "../../style";
import {
  Btn,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  SectionTitle,
  Tabs,
} from "../ui";
import CalendarAgenda from "./CalendarAgenda";
import CalendarEventDetails from "./CalendarEventDetails";
import CalendarTimeline from "./CalendarTimeline";
import { eventColor } from "./common";

// Below this the week grid is too cramped, so it's day view only
export const SMALL_QUERY = "(max-width: 700px)";
// Wide enough for the agenda beside the grid; otherwise it's a tab
export const SIDE_AGENDA_QUERY = "(min-width: 1180px)";

function rangeLabel(first, last) {
  if (first.isSame(last, "month")) {
    return `${first.date()} – ${last.format("D MMMM YYYY")}`;
  }
  return `${first.format("D MMMM")} – ${last.format("D MMMM YYYY")}`;
}

// Presentational: Calendar.js does the fetching. week/upcoming are the API's
// two lists; together they cover Monday this week to 7 days from now.
function CalendarView({ week, upcoming, isLoading, error, onRetry, now }) {
  const small = useMediaQuery(SMALL_QUERY);
  const sideAgenda = useMediaQuery(SIDE_AGENDA_QUERY);
  const [view, setView] = useState("week");
  const [day, setDay] = useState(() => startOfDay(now));
  const [selected, setSelected] = useState(null);

  let mode = view;
  if (small && mode === "week") mode = "day";
  if (sideAgenda && mode === "agenda") mode = "week";

  const events = mergeEvents(upcoming ?? [], week ?? []);
  // `upcoming` only has events starting after the fetch, so add timed ones
  // already under way from `week` for "Happening now"
  const agenda = mergeEvents(
    upcoming ?? [],
    (week ?? []).filter(
      (e) => !e.is_all_day && !e.start.isAfter(now) && e.end.isAfter(now),
    ),
  );
  const thisWeek = startOfWeek(now);
  const syncedUntil = startOfDay(now).add(7, "days");
  const weekStart = startOfWeek(day);
  const weekOffset = weekStart.diff(thisWeek, "weeks");
  const weekDays = Array.from({ length: 7 }, (_, i) =>
    weekStart.clone().add(i, "days"),
  );
  // Work week, plus the weekend when there's something on it or it's today
  const shownWeekDays = weekDays.filter(
    (d, i) => i < 5 || d.isSame(now, "day") || events.some((e) => onDay(e, d)),
  );

  const isWeek = mode === "week";
  const canPrev = isWeek ? weekOffset > 0 : day.isAfter(thisWeek, "day");
  const canNext = isWeek ? weekOffset < 1 : day.isBefore(syncedUntil, "day");
  const shift = (n) =>
    setDay((d) => {
      if (!isWeek) return d.clone().add(n, "days");
      // Land on today when moving back to this week
      const target = startOfWeek(d).add(n, "weeks");
      return target.isSame(thisWeek) ? startOfDay(now) : target;
    });

  const viewItems = [
    !small && { value: "week", label: "Week" },
    { value: "day", label: "Day" },
    !sideAgenda && { value: "agenda", label: "Agenda" },
  ].filter(Boolean);

  const subtitle =
    mode === "agenda"
      ? "Next 7 days"
      : isWeek
        ? `${rangeLabel(weekDays[0], weekDays[6])} · ${
            weekOffset === 0 ? "This week" : "Next week"
          }`
        : day.format("dddd D MMMM YYYY");

  let body;
  if (isLoading) {
    body = <LoadingState label="Loading your calendar..." />;
  } else if (error) {
    body = (
      <ErrorState onRetry={onRetry} retryLabel="Try again">
        Couldn't load your calendar.
      </ErrorState>
    );
  } else if (mode === "agenda") {
    body = (
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          paddingBottom: "24px",
        }}
      >
        <div style={{ maxWidth: "720px" }}>
          <CalendarAgenda events={agenda} now={now} onOpen={setSelected} />
        </div>
      </div>
    );
  } else {
    const nothing = !isWeek && !events.some((e) => onDay(e, day));
    body = (
      <>
        {!isWeek && (
          <DayStrip
            days={weekDays}
            selected={day}
            now={now}
            events={events}
            minDay={thisWeek}
            maxDay={syncedUntil}
            onSelect={setDay}
          />
        )}
        {nothing && (
          <EmptyState inline style={{ padding: "0 0 8px" }}>
            Nothing in your calendar.
          </EmptyState>
        )}
        <CalendarTimeline
          days={isWeek ? shownWeekDays : [day]}
          events={events}
          now={now}
          syncedUntil={syncedUntil}
          onOpen={setSelected}
        />
      </>
    );
  }

  return (
    <div style={{ display: "flex", height: "100%", minHeight: 0 }}>
      <div
        className="sk-cal-main"
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          gap: "14px",
        }}
      >
        <PageHeader
          title="Calendar"
          subtitle={subtitle}
          actions={
            <>
              {viewItems.length > 1 && (
                <Tabs
                  ariaLabel="Calendar view"
                  size="sm"
                  value={mode}
                  onChange={setView}
                  items={viewItems}
                />
              )}
              {mode !== "agenda" && (
                <div style={{ display: "flex", gap: "4px" }}>
                  <Btn
                    icon="chevronLeft"
                    aria-label={isWeek ? "Previous week" : "Previous day"}
                    title={isWeek ? "Previous week" : "Previous day"}
                    disabled={!canPrev}
                    onClick={() => shift(-1)}
                  />
                  <Btn onClick={() => setDay(startOfDay(now))}>Today</Btn>
                  <Btn
                    icon="chevronRight"
                    aria-label={isWeek ? "Next week" : "Next day"}
                    title={isWeek ? "Next week" : "Next day"}
                    disabled={!canNext}
                    onClick={() => shift(1)}
                  />
                </div>
              )}
            </>
          }
        />
        {body}
      </div>

      {sideAgenda && !isLoading && !error && (
        <aside
          aria-label="Upcoming events"
          style={{
            flex: "0 0 320px",
            boxSizing: "border-box",
            overflowY: "auto",
            padding: "28px 14px 24px",
            borderLeft: `1px solid ${colors.border}`,
            background: colors.sidebar,
          }}
        >
          <SectionTitle style={{ padding: "0 10px 12px" }}>
            Upcoming
          </SectionTitle>
          <CalendarAgenda events={agenda} now={now} onOpen={setSelected} />
        </aside>
      )}

      <CalendarEventDetails
        event={selected}
        now={now}
        onClose={() => setSelected(null)}
      />
    </div>
  );
}

// Mon to Sun chips for picking a day in day view
function DayStrip({ days, selected, now, events, minDay, maxDay, onSelect }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
        gap: "6px",
      }}
    >
      {days.map((d) => {
        const on = d.isSame(selected, "day");
        const today = d.isSame(now, "day");
        const disabled = d.isBefore(minDay, "day") || d.isAfter(maxDay, "day");
        const timed = events.filter((e) => !e.is_all_day && onDay(e, d));
        return (
          <button
            key={d.format()}
            type="button"
            className="sk-card-button"
            aria-pressed={on}
            aria-label={d.format("dddd D MMMM")}
            disabled={disabled}
            onClick={() => onSelect(d)}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "2px",
              padding: "7px 0 6px",
              borderRadius: "12px",
              border: `1px solid ${on ? colors.tealBorder : colors.border}`,
              background: on ? colors.tealTint : colors.surface,
              color: colors.text,
              font: "inherit",
              cursor: disabled ? "default" : "pointer",
              opacity: disabled || (d.isBefore(now, "day") && !on) ? 0.5 : 1,
            }}
          >
            <span
              style={{
                fontSize: "11px",
                fontWeight: 700,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: colors.textFaint,
              }}
            >
              {d.format("ddd")}
            </span>
            <span
              style={{
                fontSize: "18px",
                fontWeight: 600,
                lineHeight: 1.2,
                color: today ? colors.teal : colors.text,
              }}
            >
              {d.date()}
            </span>
            <span style={{ display: "flex", gap: "3px", height: "5px" }}>
              {timed.slice(0, 4).map((e) => (
                <i
                  key={`${e.id}-${e.start.valueOf()}`}
                  style={{
                    width: "5px",
                    height: "5px",
                    borderRadius: "50%",
                    background: eventColor(e),
                  }}
                />
              ))}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export default CalendarView;
